"use client";

import type React from "react";
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { toast } from "sonner";
import {
  requestEntireScreen,
  type InitializeScreenShareOptions,
} from "../ai-proctoring/Screencapture";

interface ProctoringContextType {
  isCameraActive: boolean;
  isMicActive: boolean;
  isFullscreen: boolean;
  mediaStream: MediaStream | null;
  screenStream: MediaStream | null;
  toggleFullscreen: () => void;
  captureSnapshot: () => Promise<string | null>;
  initializeMediaStream: (force?: boolean, silent?: boolean) => Promise<MediaStream | null>;
  initializeScreenShare: (opts?: InitializeScreenShareOptions) => Promise<MediaStream | null>;
  stopScreenShare: () => void;
  isProctoringInitialized: boolean;
  setIsProctoringInitialized: (value: boolean) => void;
  restoreProctoringState: (clearState?: boolean) => Promise<boolean>;
  cleanupProctoring: () => void;
  resetProctoringState: () => boolean;
}

const defaultContext: ProctoringContextType = {
  isCameraActive: false,
  isMicActive: false,
  isFullscreen: false,
  mediaStream: null,
  screenStream: null,
  toggleFullscreen: () => {},
  captureSnapshot: async () => null,
  initializeMediaStream: async () => null,
  initializeScreenShare: async () => null,
  stopScreenShare: () => {},
  isProctoringInitialized: false,
  setIsProctoringInitialized: () => {},
  restoreProctoringState: async () => false,
  cleanupProctoring: () => {},
  resetProctoringState: () => false,
};

const ProctoringContext = createContext<ProctoringContextType>(defaultContext);

const PROCTORING_STORAGE_KEY = "assessment_proctoring_state";
const PROCTORING_SESSION_ID_KEY = "assessment_proctoring_session_id";

export const useProctoringContext = () => useContext(ProctoringContext);

export const ProctoringProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isCameraActive, setIsCameraActive] = useState<boolean>(false);
  const [isMicActive, setIsMicActive] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
  const [screenStream, setScreenStream] = useState<MediaStream | null>(null);
  const [isProctoringInitialized, setIsProctoringInitialized] =
    useState<boolean>(false);
  const [sessionId, setSessionId] = useState<string>("");

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const allMediaStreamsRef = useRef<Set<MediaStream>>(new Set());
  const allVideoElementsRef = useRef<Set<HTMLVideoElement>>(new Set());
  const cleanupInProgressRef = useRef<boolean>(false);

  const registerMediaStream = useCallback((stream: MediaStream) => {
    console.log("Registering media stream for tracking");
    allMediaStreamsRef.current.add(stream);
  }, []);

  const registerVideoElement = useCallback((video: HTMLVideoElement) => {
    console.log("Registering video element for tracking");
    allVideoElementsRef.current.add(video);
  }, []);

  const unregisterMediaStream = useCallback((stream: MediaStream) => {
    console.log("Unregistering media stream");
    allMediaStreamsRef.current.delete(stream);
  }, []);

  const unregisterVideoElement = useCallback((video: HTMLVideoElement) => {
    console.log("Unregistering video element");
    allVideoElementsRef.current.delete(video);
  }, []);

  const initializeScreenShare =
      useCallback(async (opts?: InitializeScreenShareOptions): Promise<MediaStream | null> => {
      try {
        console.log("Initializing screen share...");

        if (screenStream && screenStream.active) {
          console.log("Using existing active screen stream");
          return screenStream;
        }

        if (screenStream) {
          console.log("Stopping existing inactive screen stream");
          screenStream.getTracks().forEach((track) => track.stop());
          unregisterMediaStream(screenStream);
        }

        // FIXED: Removed displaySurface property
        // Only "Entire Screen" is accepted. A tab/window is stopped immediately and reported to the caller.
        const { stream, rejected } = await requestEntireScreen();
        if (rejected || !stream) {
          sessionStorage.removeItem("screenShareActive");
          opts?.onRejected?.(rejected ?? "not_entire_screen");
          return null;
        }

        console.log("Screen share stream obtained successfully", {
          videoTracks: stream.getVideoTracks().length,
          active: stream.active,
        });

        stream.getVideoTracks()[0].onended = () => {
          console.log("Screen sharing ended by user");
          setScreenStream(null);
          sessionStorage.removeItem("screenShareActive");
        };

        registerMediaStream(stream);
        setScreenStream(stream);
        sessionStorage.setItem("screenShareActive", "true");

        return stream;
      } catch (error: any) {
        console.error("Error accessing screen share:", error);

        if (error.name === "NotAllowedError") {
          toast.error(
            "Screen sharing permission denied. Please allow screen sharing to continue."
          );
        } else {
          toast.error("Failed to start screen sharing. Please try again.");
        }

        sessionStorage.removeItem("screenShareActive");
        return null;
      }
    }, [screenStream, registerMediaStream, unregisterMediaStream]);

  const stopScreenShare = useCallback(() => {
    if (screenStream) {
      console.log("Stopping screen share");
      screenStream.getTracks().forEach((track) => {
        track.stop();
        console.log("Screen share track stopped");
      });
      unregisterMediaStream(screenStream);
      setScreenStream(null);
      sessionStorage.removeItem("screenShareActive");
    }
  }, [screenStream, unregisterMediaStream]);

  useEffect(() => {
    let storedSessionId = localStorage.getItem(PROCTORING_SESSION_ID_KEY);

    if (!storedSessionId) {
      storedSessionId = `proctoring_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      localStorage.setItem(PROCTORING_SESSION_ID_KEY, storedSessionId);
    }

    setSessionId(storedSessionId);
    restoreProctoringState();

    window.addEventListener("beforeunload", saveProctoringState);

    return () => {
      window.removeEventListener("beforeunload", saveProctoringState);
    };
  }, []);

  const saveProctoringState = useCallback(() => {
    try {
      if (isProctoringInitialized) {
        localStorage.setItem(
          PROCTORING_STORAGE_KEY,
          JSON.stringify({
            isProctoringInitialized,
            isCameraActive,
            isMicActive,
            isFullscreen,
            screenShareActive: !!screenStream && screenStream.active,
            timestamp: Date.now(),
          })
        );
        console.log("Proctoring state saved to localStorage");
      }
    } catch (error) {
      console.error("Error saving proctoring state:", error);
    }
  }, [
    isProctoringInitialized,
    isCameraActive,
    isMicActive,
    isFullscreen,
    screenStream,
  ]);

  useEffect(() => {
    if (isProctoringInitialized) {
      saveProctoringState();
    }
  }, [
    isProctoringInitialized,
    isCameraActive,
    isMicActive,
    isFullscreen,
    screenStream,
    saveProctoringState,
  ]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const newFullscreenState = !!document.fullscreenElement;
      setIsFullscreen(newFullscreenState);
      console.log("Fullscreen state changed:", newFullscreenState);

      if (isProctoringInitialized && !newFullscreenState) {
        console.warn("Fullscreen exited during proctoring");
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    setIsFullscreen(!!document.fullscreenElement);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, [isProctoringInitialized]);

  useEffect(() => {
    return () => {
      cleanupProctoring();
    };
  }, []);

  const restoreProctoringState = async (
    clearState?: boolean
  ): Promise<boolean> => {
    try {
      if (clearState === true) {
        console.log("Clearing proctoring state as requested");
        cleanupProctoring();
        return true;
      }

      const isRestoring = localStorage.getItem("proctoring_restoring");
      if (isRestoring === "true") {
        console.log(
          "Already restoring proctoring state, skipping duplicate call"
        );
        return isProctoringInitialized;
      }

      localStorage.setItem("proctoring_restoring", "true");

      const savedState = localStorage.getItem(PROCTORING_STORAGE_KEY);
      if (!savedState) {
        localStorage.removeItem("proctoring_restoring");
        return false;
      }

      const parsedState = JSON.parse(savedState);

      const isRecent = Date.now() - parsedState.timestamp < 60 * 60 * 1000;
      if (!isRecent) {
        console.log("Saved proctoring state is too old, not restoring");
        localStorage.removeItem("proctoring_restoring");
        return false;
      }

      console.log("Restoring proctoring state:", parsedState);

      setIsProctoringInitialized(parsedState.isProctoringInitialized || false);
      setIsCameraActive(parsedState.isCameraActive || false);
      setIsMicActive(parsedState.isMicActive || false);

      if (parsedState.isProctoringInitialized) {
        if (parsedState.isFullscreen && !document.fullscreenElement) {
          try {
            if (document.fullscreenEnabled) {
              await document.documentElement
                .requestFullscreen()
                .catch((err) => {
                  console.error("Error requesting fullscreen on restore:", err);
                });
            }
          } catch (err) {
            console.error("Error requesting fullscreen on restore:", err);
          }
        }

        if (parsedState.isCameraActive && !mediaStream) {
          try {
            const stream = await initializeMediaStream();
            if (stream) {
              console.log("Media stream restored successfully");
            }
          } catch (error) {
            console.error("Error restoring media stream:", error);
          }
        }

        if (parsedState.screenShareActive) {
          console.log(
            "Screen share was active, but will be re-requested when needed"
          );
          sessionStorage.setItem("screenShareActive", "true");
        }

        localStorage.removeItem("proctoring_restoring");
        return true;
      }

      localStorage.removeItem("proctoring_restoring");
      return parsedState.isProctoringInitialized || false;
    } catch (error) {
      console.error("Error restoring proctoring state:", error);
      localStorage.removeItem("proctoring_restoring");
      return false;
    }
  };

  const cleanupProctoring = () => {
    if (cleanupInProgressRef.current) {
      console.log("Cleanup already in progress, skipping duplicate call");
      return;
    }

    cleanupInProgressRef.current = true;
    console.log("🧹 Starting comprehensive proctoring cleanup...");

    try {
      console.log("📹 Stopping all tracked media streams...");
      allMediaStreamsRef.current.forEach((stream) => {
        try {
          stream.getTracks().forEach((track) => {
            if (track.readyState !== "ended") {
              track.stop();
              console.log(`✅ Stopped track: ${track.kind}`);
            }
          });
        } catch (error) {
          console.error("Error stopping tracked stream:", error);
        }
      });

      if (mediaStream) {
        console.log("📹 Stopping main media stream...");
        try {
          mediaStream.getTracks().forEach((track) => {
            if (track.readyState !== "ended") {
              track.stop();
            }
          });
        } catch (error) {
          console.error("Error stopping main media stream:", error);
        }
        setMediaStream(null);
      }

      if (screenStream) {
        console.log("🖥️ Stopping screen stream...");
        try {
          screenStream.getTracks().forEach((track) => {
            if (track.readyState !== "ended") {
              track.stop();
            }
          });
        } catch (error) {
          console.error("Error stopping screen stream:", error);
        }
        setScreenStream(null);
      }

      console.log("📺 Cleaning up all tracked video elements...");
      allVideoElementsRef.current.forEach((video) => {
        try {
          if (video.srcObject) {
            const stream = video.srcObject as MediaStream;
            stream.getTracks().forEach((track) => {
              if (track.readyState !== "ended") {
                track.stop();
              }
            });
            video.srcObject = null;
          }
          video.pause();
          video.removeAttribute("src");
          video.load();
        } catch (error) {
          console.error("Error cleaning up tracked video element:", error);
        }
      });

      console.log("🔍 Finding and cleaning up all DOM video elements...");
      const allVideoElements = document.querySelectorAll("video");
      allVideoElements.forEach((video, index) => {
        try {
          if (video.srcObject) {
            const stream = video.srcObject as MediaStream;
            if (stream && stream.getTracks) {
              stream.getTracks().forEach((track) => {
                if (track.readyState !== "ended") {
                  track.stop();
                }
              });
            }
            video.srcObject = null;
          }
          video.pause();
          video.removeAttribute("src");
          video.load();
        } catch (error) {
          console.error(`Error cleaning up DOM video element ${index}:`, error);
        }
      });

      if (videoRef.current) {
        console.log("🧹 Cleaning up video ref...");
        try {
          if (videoRef.current.srcObject) {
            const stream = videoRef.current.srcObject as MediaStream;
            stream.getTracks().forEach((track) => {
              if (track.readyState !== "ended") {
                track.stop();
              }
            });
            videoRef.current.srcObject = null;
          }
          videoRef.current.pause();
          videoRef.current.removeAttribute("src");
          videoRef.current.load();
        } catch (error) {
          console.error("Error cleaning up video ref:", error);
        }
      }

      if (document.fullscreenElement) {
        console.log("🖥️ Exiting fullscreen...");
        document.exitFullscreen().catch((err) => {
          console.error("Error exiting fullscreen during cleanup:", err);
        });
      }

      console.log("🗑️ Clearing tracking sets...");
      allMediaStreamsRef.current.clear();
      allVideoElementsRef.current.clear();

      console.log("💾 Clearing proctoring storage items...");
      localStorage.removeItem(PROCTORING_STORAGE_KEY);
      localStorage.removeItem("proctoring_restoring");
      localStorage.removeItem("proctoring_initializing");
      sessionStorage.removeItem("screenShareActive");

      console.log("🔄 Resetting all state...");
      setIsCameraActive(false);
      setIsMicActive(false);
      setIsFullscreen(false);
      setIsProctoringInitialized(false);

      console.log("✅ Comprehensive proctoring cleanup completed successfully");
    } catch (error) {
      console.error("❌ Error during comprehensive cleanup:", error);
    } finally {
      cleanupInProgressRef.current = false;
    }
  };

  const resetProctoringState = useCallback(() => {
    console.log("Completely resetting proctoring state");
    cleanupProctoring();
    localStorage.removeItem(PROCTORING_SESSION_ID_KEY);
    setSessionId("");
    console.log("Proctoring state completely reset");
    return true;
  }, []);

  const initializeMediaStream = async (force: boolean = false, silent: boolean = false) => {
    try {
      console.log("Initializing media stream...");

      if (isStreamHealthy(mediaStream)) {
        console.log("Using existing healthy media stream");
        return mediaStream;
      }

      const isInitializing = localStorage.getItem("proctoring_initializing");
      if (isInitializing === "true") {
        console.log(
          "Media stream initialization already in progress, waiting..."
        );

        for (let i = 0; i < 10; i++) {
          await new Promise((resolve) => setTimeout(resolve, 200));
          if (mediaStream && mediaStream.active) {
            console.log("Media stream became available while waiting");
            return mediaStream;
          }
        }

        localStorage.removeItem("proctoring_initializing");
      }

      localStorage.setItem("proctoring_initializing", "true");

      if (mediaStream) {
        console.log("Stopping existing inactive media stream");
        mediaStream.getTracks().forEach((track) => track.stop());
        unregisterMediaStream(mediaStream);
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 640, max: 1280 },
          height: { ideal: 480, max: 720 },
          facingMode: "user",
          frameRate: { ideal: 30, max: 30 },
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: false,
        },
      });

      console.log("Media stream obtained successfully");

      registerMediaStream(stream);
      bindTrackListeners(stream);
      setMediaStream(stream);
      setIsCameraActive(true);
      setIsMicActive(true);
      setIsProctoringInitialized(true);

      localStorage.setItem(
        PROCTORING_STORAGE_KEY,
        JSON.stringify({
          isProctoringInitialized: true,
          isCameraActive: true,
          isMicActive: true,
          isFullscreen,
          timestamp: Date.now(),
        })
      );

      localStorage.removeItem("proctoring_initializing");

      return stream;
    } catch (error) {
      console.error("Error accessing media devices:", error);

      try {
        console.log("Trying with minimal constraints...");
        const fallbackStream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true,
        });

        registerMediaStream(fallbackStream);
        bindTrackListeners(fallbackStream);
        setMediaStream(fallbackStream);
        setIsCameraActive(true);
        setIsMicActive(true);
        setIsProctoringInitialized(true);

        localStorage.removeItem("proctoring_initializing");
        return fallbackStream;
      } catch (fallbackError) {
        console.error("Fallback media access also failed:", fallbackError);
        if (!silent) {
          toast.error(
            "Failed to access camera or microphone. Please ensure permissions are granted."
          );
        }
        setIsCameraActive(false);
        setIsMicActive(false);
        localStorage.removeItem("proctoring_initializing");
        return null;
      }
    }
  };

    // MediaStream.active stays true while ANY track is live, so check every track
  const isStreamHealthy = (s: MediaStream | null) =>
    !!s && s.active && s.getTracks().every((t) => t.readyState === "live");

  // keep the camera/mic flags truthful when a device is unplugged
  const bindTrackListeners = (stream: MediaStream) => {
    stream.getTracks().forEach((track) => {
      track.addEventListener("ended", () => {
        if (track.kind === "video") setIsCameraActive(false);
        else setIsMicActive(false);
      });
    });
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      console.log("Requesting fullscreen");
      document.documentElement.requestFullscreen().catch((err) => {
        console.error("Error requesting fullscreen:", err);
        toast.error("Failed to enter fullscreen mode. Please try again.");
      });
    } else {
      console.log("Exiting fullscreen");
      document.exitFullscreen().catch((err) => {
        console.error("Error exiting fullscreen:", err);
      });
    }
  };

  const captureSnapshot = async (): Promise<string | null> => {
    try {
      console.log("Capturing snapshot...");

      if (!mediaStream) {
        console.log("No media stream, initializing...");
        const stream = await initializeMediaStream();
        if (!stream) {
          console.error("Failed to initialize media stream for snapshot");
          return null;
        }
      }

      if (!canvasRef.current) {
        canvasRef.current = document.createElement("canvas");
        canvasRef.current.width = 640;
        canvasRef.current.height = 480;
      }

      const canvas = canvasRef.current;
      const context = canvas.getContext("2d");

      if (!context) {
        console.error("Failed to get canvas context");
        return null;
      }

      let videoWidth = 640;
      let videoHeight = 480;

      if (mediaStream) {
        const videoTrack = mediaStream.getVideoTracks()[0];
        if (videoTrack) {
          const settings = videoTrack.getSettings();
          if (settings.width && settings.height) {
            videoWidth = settings.width;
            videoHeight = settings.height;
          }
        }
      }

      canvas.width = videoWidth;
      canvas.height = videoHeight;

      const videoElementFromDOM = document.querySelector(
        'video[ref="videoRef"]'
      );
      const videoElement =
        videoElementFromDOM instanceof HTMLVideoElement
          ? videoElementFromDOM
          : (videoRef.current as HTMLVideoElement | null);

      if (
        videoElement &&
        videoElement instanceof HTMLVideoElement &&
        videoElement.srcObject === mediaStream
      ) {
        try {
          context.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
        } catch (err) {
          console.error("Error capturing from existing video element:", err);
        }
      }

      const tempVideo = document.createElement("video");
      tempVideo.srcObject = mediaStream;
      tempVideo.muted = true;
      tempVideo.playsInline = true;
      tempVideo.autoplay = true;
      tempVideo.crossOrigin = "anonymous";

      registerVideoElement(tempVideo);

      await new Promise<void>((resolve) => {
        tempVideo.onloadedmetadata = () => {
          tempVideo
            .play()
            .then(() => {
              console.log("Temp video playing for snapshot");
              setTimeout(resolve, 300);
            })
            .catch((err) => {
              console.error("Error playing temp video:", err);
              resolve();
            });
        };

        setTimeout(resolve, 1000);
      });

      try {
        context.drawImage(tempVideo, 0, 0, canvas.width, canvas.height);
      } catch (err) {
        console.error("Error drawing to canvas:", err);
        try {
          context.drawImage(tempVideo, 0, 0, 640, 480);
        } catch (innerErr) {
          console.error("Fallback drawing also failed:", innerErr);
          return null;
        }
      }

      try {
        if (tempVideo.srcObject) {
          tempVideo.srcObject = null;
        }
        tempVideo.pause();
        tempVideo.removeAttribute("src");
        tempVideo.load();
        unregisterVideoElement(tempVideo);
        console.log("✅ Temporary video element cleaned up after snapshot");
      } catch (cleanupErr) {
        console.error("Error cleaning up temporary video:", cleanupErr);
      }

      const dataUrl = canvas.toDataURL("image/jpeg", 0.85);

      if (
        !dataUrl ||
        dataUrl === "data:," ||
        dataUrl === "data:image/jpeg;base64,"
      ) {
        console.error("Failed to generate data URL from canvas");
        return null;
      }

      console.log("Snapshot captured successfully");
      return dataUrl;
    } catch (error) {
      console.error("Error in captureSnapshot:", error);
      return null;
    }
  };

  return (
    <ProctoringContext.Provider
      value={{
        isCameraActive,
        isMicActive,
        isFullscreen,
        mediaStream,
        screenStream,
        toggleFullscreen,
        captureSnapshot,
        initializeMediaStream,
        initializeScreenShare,
        stopScreenShare,
        isProctoringInitialized,
        setIsProctoringInitialized,
        restoreProctoringState,
        cleanupProctoring,
        resetProctoringState,
      }}
    >
      {children}
    </ProctoringContext.Provider>
  );
};
